<template>
  <CModal :show="show" :closeOnBackdrop="false" :centered="true" size="lg">
    <template #header>
      <h5 class="modal-title">{{ title }}</h5>
    </template>
    <div v-if="!loading">
      <form @submit.prevent="onSubmit">
        <div class="form-group position-relative z-3">
          <label>{{ $t("models.mpi.island") }}</label>
          <input
            type="text"
            class="form-control"
            v-model="islandSearch"
            @input="onIslandSearch"
            @focus="showIslandDropdown = true"
            @blur="hideIslandDropdown"
            placeholder=""
            autocomplete="off"
          />
          <div
            v-if="showIslandDropdown && filteredIslands.length > 0"
            class="list-group position-absolute w-100 bg-white"
            style="z-index: 1000; max-height: 200px; overflow-y: auto"
          >
            <button
              type="button"
              v-for="island in filteredIslands"
              :key="island.id"
              class="list-group-item list-group-item-action"
              @mousedown.prevent="selectIsland(island)"
            >
              {{ island.role.name }}
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpi.price") }}</label>
          <CInput v-model.number="form.price" type="number" min="0" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.mpi.order_offset") }}</label>
          <CInput v-model.number="form.offset" type="number" min="0" />
        </div>
      </form>

      <CAlert v-if="validationError" color="danger" :show.sync="showAlert">
        {{ validationError }}
      </CAlert>
    </div>
    <div
      v-else
      class="d-flex justify-content-center align-items-center"
      style="height: 200px"
    >
      <CSpinner color="primary" />
    </div>

    <template #footer>
      <CButton color="primary" variant="outline" @click.prevent="closeModal">
        {{ $t("common.editor.cancel") }}
      </CButton>
      <vue-ladda
        :loading="loading"
        data-style="zoom-in"
        button-class="btn btn-primary px-4"
        @click.prevent="onSubmit"
      >
        {{ $t("common.editor.confirm") }}
      </vue-ladda>
    </template>
  </CModal>
</template>

<script>
import { apiPatch, apiPost, apiGet } from "../../../helpers/apiHelper";

export default {
  name: "IslandModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: [Number, String],
      default: null,
    },
    values: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loading: false,
      validationError: null,
      showAlert: false,
      islandSearch: "",
      showIslandDropdown: false,
      form: {
        island: { role: { name: null, id: null } },
        price: 0,
        offset: 0,
      },
      islandData: [],
    };
  },

  computed: {
    filteredIslands() {
      if (!this.islandSearch || this.islandSearch.length < 2) {
        return [];
      }
      const search = this.islandSearch.toLowerCase();
      return this.islandData.filter((i) =>
        i.role.name.toLowerCase().includes(search)
      );
    },
  },

  watch: {
    show: {
      handler(isShown) {
        if (isShown) {
          if (this.values && Object.keys(this.values).length > 0) {
            this.populateForm(this.values);
          } else {
            this.resetForm();
          }
        }
      },
      immediate: false,
    },
    values: {
      handler(newValues) {
        if (newValues && Object.keys(newValues).length > 0) {
          this.populateForm(newValues);
        }
      },
      immediate: true,
    },
  },

  methods: {
    onIslandSearch() {
      this.showIslandDropdown = true;
    },

    selectIsland(island) {
      this.form.island = {
        role: {
          id: island.role.name,
          name: island.id,
        },
      };
      this.islandSearch = island.role.name;
      this.showIslandDropdown = false;
    },

    hideIslandDropdown() {
      setTimeout(() => {
        this.showIslandDropdown = false;
      }, 200);
    },

    populateForm(data) {
      this.form.island = {
        role: {
          id: data.island?.role?.name || null,
          name: data.island?.id || null,
        },
      };
      this.islandSearch = data.island?.role?.name || "";
      this.form.price = data.price || 0;
      this.form.offset = data.offset || 0;
      this.loading = false;
    },

    async fetchIslands() {
      const response = await apiGet(
        "role_marketisland",
        [this.$store.state.role.id],
        null
      );
      if (response && response.status === 200) {
        this.islandData = response.data.payload.Items;
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error loading island data",
          color: "danger",
          autohide: true,
        });
      }
    },

    async onSubmit() {
      this.validationError = null;
      this.showAlert = false;

      // Validation
      if (!this.form.island?.role?.id) {
        this.validationError = this.$t("models.mpi.validation.island");
        this.showAlert = true;
        return;
      }

      const requestParams = this.form;

      // check user permissions before submitting
      const perm = this.$store.state.connections.current.permissions.role_lg;
      if (perm !== 2) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.permissions"
        );
        this.showAlert = true;
        return;
      }

      let response;
      if (this.editMode && this.editId) {
        response = await apiPatch(
          "role_marketisland_connections",
          this.editId,
          requestParams,
          [this.$store.state.role.id]
        );
      } else {
        response = await apiPost(
          "role_marketisland_connections",
          requestParams,
          [this.$store.state.role.id]
        );
      }

      if (response) {
        this.$emit("submitted");
        this.closeModal();
      }
    },

    closeModal() {
      this.$emit("update:show", false);
      this.validationError = null;
      this.showAlert = false;
    },

    resetForm() {
      this.form = {
        island: { role: { name: null, id: null } },
        price: 0,
        offset: 0,
      };
      this.islandSearch = "";
      this.validationError = null;
      this.showAlert = false;
    },
  },

  async created() {
    await this.fetchIslands();
  },
};
</script>
